// ============================================================
// PURCHASES MODULE — supplier purchase invoices, GST portal import,
// and payments made to suppliers.
// src/Purchases.js
// ============================================================

import { useState, useEffect, useCallback, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";
import { fmtDMY } from "./dateUtils";
import { matchItems } from "./matchItems";
import { useAuth } from "./Auth";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const fmt = (n) => "₹" + parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
const num = (v) => { const n = parseFloat(String(v ?? "").replace(/,/g,"")); return isNaN(n) ? 0 : n; };
const round2 = (n) => Math.round(n*100)/100;
const todayISO = () => new Date().toISOString().slice(0,10);

const PAY_MODES = { bank:"Bank transfer", upi:"UPI", cash:"Cash", cheque:"Cheque", other:"Other" };
const DOC_LABELS = { invoice:"Invoice", credit_note:"Credit note", debit_note:"Debit note" };

const S = {
  pageHeader: {display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:24,paddingBottom:20,borderBottom:"1px solid var(--paper3)",flexWrap:"wrap",gap:12},
  pageTitle:  {fontFamily:"var(--serif)",fontSize:29,fontWeight:300,letterSpacing:"-0.5px"},
  subtitle:   {fontSize:12,color:"var(--ink3)",marginTop:6},
  btnPrimary: {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"none",background:"var(--accent)",color:"#fff",fontWeight:500},
  btnGhost:   {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"},
  btnSm:      {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)"},
  card:       {background:"#fff",borderRadius:10,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"},
  th:         (r) => ({padding:"10px 14px",textAlign:r?"right":"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,whiteSpace:"nowrap"}),
  td:         {padding:"11px 14px",fontSize:13,color:"var(--ink)"},
  input:      {padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%",boxSizing:"border-box"},
  label:      {fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,display:"block",marginBottom:5},
  overlay:    {position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
  modal:      {background:"#fff",borderRadius:14,width:"100%",maxHeight:"92vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"},
  pill:       (on,c) => ({padding:"5px 12px",borderRadius:20,fontSize:12,cursor:"pointer",fontFamily:"var(--mono)",border:`1px solid ${on?c:"var(--paper3)"}`,background:on?c:"#fff",color:on?"#fff":"var(--ink2)"}),
};

// The API caps responses at 1000 rows, so page through anything that can grow.
async function fetchAllRows(build) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return out;
}

async function loadXLSX() {
  if (window.XLSX) return window.XLSX;
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    s.onload = resolve; s.onerror = () => reject(new Error("Could not load the Excel reader (check your internet connection)."));
    document.head.appendChild(s);
  });
  return window.XLSX;
}

function isoDate(v) {
  if (v instanceof Date && !isNaN(v)) return v.toISOString().slice(0,10);
  const s = String(v ?? "").trim();
  let m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}`;
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
}

// ── GST PORTAL (GSTR-2A / 2B) PARSER ─────────────────────────
// Every invoice appears as one row per tax rate followed by a "<number>-Total"
// row. The Total row carries the invoice value and summed taxes, so it is used
// for the invoice; rate rows are only a fallback if a Total row is missing.
// The B2B sheet holds invoices; the CDNR sheet holds credit/debit notes.
async function parseGstFile(file) {
  const XLSX = await loadXLSX();
  const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type:"array" });
  // The portal's files declare a too-small sheet range (e.g. A1:X30 for 580 rows),
  // so recompute it from the cells that really exist, always anchored at A1.
  const rows = (name) => {
    const ws = wb.Sheets[name];
    if (!ws) return null;
    let maxR = 0, maxC = 0;
    Object.keys(ws).forEach(k => {
      if (k[0] === "!") return;
      const c = XLSX.utils.decode_cell(k);
      if (c.r > maxR) maxR = c.r;
      if (c.c > maxC) maxC = c.c;
    });
    ws["!ref"] = XLSX.utils.encode_range({ r:0, c:0 }, { r:maxR, c:maxC });
    return XLSX.utils.sheet_to_json(ws, { header:1, defval:"" });
  };
  const b2b = rows("B2B");
  if (!b2b || !/gstin of supplier/i.test(String(b2b[4]?.[0] || ""))) {
    throw new Error("This doesn't look like a GSTR-2A/2B Excel from the GST portal (no B2B sheet with the expected headings).");
  }
  const readMe = rows("Read me") || [];
  const taxPeriod = String(readMe.find(r => /tax period/i.test(String(r[3] || "")))?.[4] || "");

  const collect = (data, layout) => {
    const map = new Map();
    data.slice(6).forEach(r => {
      const gstin = String(r[0] || "").trim();
      const rawNo = String(r[layout.no] || "").trim();
      if (!gstin || !rawNo) return;
      const isTotal = /-total$/i.test(rawNo);
      const no = isTotal ? rawNo.replace(/-total$/i, "") : rawNo;
      const type = layout.type ? (/debit/i.test(String(r[layout.type])) ? "debit_note" : "credit_note") : "invoice";
      const key = `${gstin}|${no}|${type}`;
      const cur = map.get(key) || { gstin, name:String(r[1]||"").trim(), doc_type:type, invoice_number:no,
        invoice_date:isoDate(r[layout.date]), invoice_value:num(r[layout.value]), place_of_supply:String(r[layout.pos]||"").trim(),
        reverse_charge:String(r[layout.rcm]).trim().toUpperCase()==="Y", irn:String(r[layout.irn]||"").trim() || null,
        rate:{taxable_value:0,igst:0,cgst:0,sgst:0,cess:0}, total:null };
      const amounts = { taxable_value:num(r[layout.taxable]), igst:num(r[layout.igst]), cgst:num(r[layout.cgst]), sgst:num(r[layout.sgst]), cess:num(r[layout.cess]) };
      if (isTotal) cur.total = amounts;
      else Object.keys(amounts).forEach(k => { cur.rate[k] += amounts[k]; });
      map.set(key, cur);
    });
    return [...map.values()].map(d => {
      const a = d.total || d.rate;
      const { rate, total, ...rest } = d;
      return { ...rest, taxable_value:round2(a.taxable_value), igst:round2(a.igst), cgst:round2(a.cgst), sgst:round2(a.sgst), cess:round2(a.cess), tax_period:taxPeriod || null, source:"gst_2a" };
    });
  };

  const docs = collect(b2b, { no:2, date:4, value:5, pos:6, rcm:7, taxable:9, igst:10, cgst:11, sgst:12, cess:13, irn:22 });
  const cdnr = rows("CDNR");
  if (cdnr) docs.push(...collect(cdnr, { type:2, no:3, date:5, value:6, pos:7, rcm:8, taxable:10, igst:11, cgst:12, sgst:13, cess:14, irn:23 }));

  // Amendment sheets aren't imported; tell the user when they contain data.
  const countData = (name, from) => (rows(name) || []).slice(from).filter(r => r.some(c => String(c).trim() !== "")).length;
  const amendments = countData("B2BA", 7) + countData("CDNRA", 7);
  return { docs, taxPeriod, amendments };
}

// ── PURCHASE INVOICE MODAL (manual add / edit) ────────────────
function InvoiceModal({ suppliers, record, onClose, onSaved }) {
  const isEdit = !!record;
  const [supplierId, setSupplierId] = useState(record?.supplier_id || "");
  const [supQ, setSupQ]       = useState(record?.suppliers?.name || "");
  const [showSup, setShowSup] = useState(false);
  const [newName, setNewName] = useState("");
  const [newGstin, setNewGstin] = useState("");
  const [docType, setDocType] = useState(record?.doc_type || "invoice");
  const [number, setNumber]   = useState(record?.invoice_number || "");
  const [date, setDate]       = useState(record?.invoice_date || todayISO());
  const [taxable, setTaxable] = useState(record ? String(record.taxable_value) : "");
  const [cgst, setCgst]       = useState(record ? String(record.cgst) : "");
  const [sgst, setSgst]       = useState(record ? String(record.sgst) : "");
  const [igst, setIgst]       = useState(record ? String(record.igst) : "");
  const [cess, setCess]       = useState(record ? String(record.cess) : "");
  const [rcm, setRcm]         = useState(!!record?.reverse_charge);
  const [value, setValue]     = useState(record ? String(record.invoice_value) : "");
  const [valueTouched, setValueTouched] = useState(isEdit);
  const [notes, setNotes]     = useState(record?.notes || "");
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");

  const computed = round2(num(taxable) + (rcm ? 0 : num(cgst) + num(sgst) + num(igst) + num(cess)));
  const shownValue = valueTouched ? value : (computed ? String(computed) : "");
  const { list: supMatches } = matchItems(suppliers, supQ, s => `${s.name} ${s.gstin||""}`, 8);

  const save = async () => {
    setError("");
    if (!supplierId && !newName.trim()) return setError("Pick a supplier or enter a new supplier's name.");
    if (!number.trim()) return setError("Enter the invoice number.");
    if (!date) return setError("Enter the invoice date.");
    if (!(num(shownValue) > 0)) return setError("The invoice total must be more than zero.");
    setSaving(true);
    try {
      let sid = supplierId;
      if (!sid) {
        const gstin = newGstin.trim().toUpperCase() || null;
        const { data, error: se } = await supabase.from("suppliers").insert({ name: newName.trim(), gstin }).select("id").single();
        if (se) throw se;
        sid = data.id;
      }
      const payload = { supplier_id:sid, doc_type:docType, invoice_number:number.trim(), invoice_date:date,
        taxable_value:num(taxable), cgst:num(cgst), sgst:num(sgst), igst:num(igst), cess:num(cess),
        invoice_value:num(shownValue), reverse_charge:rcm, notes:notes.trim() || null, updated_at:new Date().toISOString() };
      const { error: ie } = isEdit
        ? await supabase.from("purchase_invoices").update(payload).eq("id", record.id)
        : await supabase.from("purchase_invoices").insert({ ...payload, source:"manual" });
      if (ie) throw ie;
      onSaved();
    } catch (e) {
      setError(/purchase_invoices_uniq|duplicate/i.test(e.message) ? "That supplier already has a document with this number." : e.message);
    } finally { setSaving(false); }
  };

  const field = (label, val, set, extra={}) => (
    <div><label style={S.label}>{label}</label><input style={S.input} value={val} onChange={e=>set(e.target.value)} {...extra}/></div>
  );

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{...S.modal,maxWidth:640}}>
        <div style={{padding:"22px 26px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:"var(--serif)",fontSize:20,fontWeight:300}}>{isEdit?"Edit purchase document":"Add purchase invoice"}</div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--ink3)"}}>✕</button>
        </div>
        <div style={{padding:"16px 26px 26px",display:"grid",gap:14}}>
          <div style={{position:"relative"}}>
            <label style={S.label}>Supplier *</label>
            <input style={S.input} value={supQ} placeholder="Search supplier by name or GSTIN…"
              onFocus={()=>setShowSup(true)}
              onChange={e=>{ setSupQ(e.target.value); setSupplierId(""); setShowSup(true); }}/>
            {showSup && !supplierId && supQ && (
              <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--paper3)",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",zIndex:20,maxHeight:220,overflowY:"auto"}}>
                {supMatches.map(s=>(
                  <div key={s.id} style={{padding:"8px 12px",fontSize:12,cursor:"pointer",borderBottom:"1px solid var(--paper2)"}}
                    onMouseDown={()=>{ setSupplierId(s.id); setSupQ(s.name); setShowSup(false); }}>
                    <div style={{fontWeight:500}}>{s.name}</div><div style={{fontSize:10,color:"var(--ink3)"}}>{s.gstin||"no GSTIN"}</div>
                  </div>
                ))}
                {supMatches.length === 0 && <div style={{padding:"8px 12px",fontSize:12,color:"var(--ink3)"}}>No match — fill in the new supplier below.</div>}
              </div>
            )}
          </div>
          {!supplierId && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              {field("New supplier name", newName, setNewName, {placeholder:"Only if not in the list"})}
              {field("New supplier GSTIN", newGstin, setNewGstin, {placeholder:"Optional"})}
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
            <div><label style={S.label}>Type</label>
              <select style={S.input} value={docType} onChange={e=>setDocType(e.target.value)}>
                {Object.entries(DOC_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
              </select></div>
            {field("Number *", number, setNumber)}
            <div><label style={S.label}>Date *</label><input type="date" style={S.input} value={date} onChange={e=>setDate(e.target.value)}/></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10}}>
            {field("Taxable", taxable, setTaxable, {type:"number"})}
            {field("CGST", cgst, setCgst, {type:"number"})}
            {field("SGST", sgst, setSgst, {type:"number"})}
            {field("IGST", igst, setIgst, {type:"number"})}
            {field("Cess", cess, setCess, {type:"number"})}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,alignItems:"end"}}>
            <div><label style={S.label}>Total payable to supplier *</label>
              <input style={S.input} type="number" value={shownValue} onChange={e=>{ setValue(e.target.value); setValueTouched(true); }}/></div>
            <label style={{display:"flex",gap:8,alignItems:"center",fontSize:12,color:"var(--ink2)",paddingBottom:8}}>
              <input type="checkbox" checked={rcm} onChange={e=>setRcm(e.target.checked)}/> Reverse charge (tax not billed by supplier)
            </label>
          </div>
          {field("Notes", notes, setNotes)}
          {error && <div style={{color:"var(--danger)",fontSize:12}}>{error}</div>}
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":isEdit?"Save changes":"Add"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SUPPLIER PICKER ───────────────────────────────────────────
function SupplierPicker({ suppliers, value, onChange, label="Supplier *" }) {
  const [q, setQ] = useState(suppliers.find(s => s.id === value)?.name || "");
  const [open, setOpen] = useState(false);
  const { list } = matchItems(suppliers, q, s => `${s.name} ${s.gstin||""}`, 8);
  return (
    <div style={{position:"relative"}}>
      <label style={S.label}>{label}</label>
      <input style={S.input} value={q} placeholder="Search supplier by name or GSTIN…"
        onFocus={()=>setOpen(true)} onChange={e=>{ setQ(e.target.value); onChange(""); setOpen(true); }}/>
      {open && !value && q && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--paper3)",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",zIndex:20,maxHeight:220,overflowY:"auto"}}>
          {list.map(s=>(
            <div key={s.id} style={{padding:"8px 12px",fontSize:12,cursor:"pointer",borderBottom:"1px solid var(--paper2)"}}
              onMouseDown={()=>{ onChange(s.id); setQ(s.name); setOpen(false); }}>
              <div style={{fontWeight:500}}>{s.name}</div><div style={{fontSize:10,color:"var(--ink3)"}}>{s.gstin||"no GSTIN"}</div>
            </div>
          ))}
          {list.length === 0 && <div style={{padding:"8px 12px",fontSize:12,color:"var(--ink3)"}}>No supplier matches.</div>}
        </div>
      )}
      {value && <div style={{fontSize:11,color:"var(--ink3)",marginTop:4,fontFamily:"var(--mono)"}}>GSTIN {suppliers.find(s => s.id === value)?.gstin || "—"}</div>}
    </div>
  );
}

// ── PAYMENT MODAL ─────────────────────────────────────────────
// Payments belong to the supplier's running ledger. Linking one to a specific
// invoice is optional — most are part-payments "on account".
function PayModal({ suppliers, rows, supplierId: initialSupplier, invoice, record, onClose, onSaved }) {
  const isEdit = !!record;
  const [supplierId, setSupplierId] = useState(record?.supplier_id || invoice?.supplier_id || initialSupplier || "");
  const [invoiceId, setInvoiceId]   = useState(record?.purchase_invoice_id || invoice?.id || "");
  const [date, setDate]     = useState(record?.payment_date || todayISO());
  const [amount, setAmount] = useState(record ? String(record.amount) : invoice && invoice.balance > 0 ? String(invoice.balance) : "");
  const [mode, setMode]     = useState(record?.mode || "bank");
  const [ref, setRef]       = useState(record?.reference_no || "");
  const [notes, setNotes]   = useState(record?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const open = rows.filter(r => r.supplier_id === supplierId && r.doc_type !== "credit_note" && (r.balance > 0.005 || r.id === invoiceId))
    .sort((a,b) => a.invoice_date.localeCompare(b.invoice_date));
  const supplier = suppliers.find(s => s.id === supplierId);

  const save = async () => {
    setError("");
    if (!supplierId) return setError("Pick the supplier.");
    if (!(num(amount) > 0)) return setError("Enter the amount paid.");
    setSaving(true);
    const payload = { supplier_id: supplierId, purchase_invoice_id: invoiceId || null, payment_date: date, amount: num(amount),
      mode, reference_no: ref.trim() || null, notes: notes.trim() || null };
    const { error: e } = isEdit
      ? await supabase.from("purchase_payments").update(payload).eq("id", record.id)
      : await supabase.from("purchase_payments").insert(payload);
    setSaving(false);
    if (e) return setError(e.message);
    onSaved();
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{...S.modal,maxWidth:520}}>
        <div style={{padding:"22px 26px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:"var(--serif)",fontSize:20,fontWeight:300}}>{isEdit ? "Edit payment" : "Record payment"}{supplier ? ` — ${supplier.name}` : ""}</div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--ink3)"}}>✕</button>
        </div>
        <div style={{padding:"16px 26px 26px",display:"grid",gap:14}}>
          {!initialSupplier && !invoice && !isEdit && <SupplierPicker suppliers={suppliers} value={supplierId} onChange={id=>{ setSupplierId(id); setInvoiceId(""); }}/>}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div><label style={S.label}>Date</label><input type="date" style={S.input} value={date} onChange={e=>setDate(e.target.value)}/></div>
            <div><label style={S.label}>Amount *</label><input type="number" style={S.input} value={amount} onChange={e=>setAmount(e.target.value)}/></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div><label style={S.label}>Mode</label>
              <select style={S.input} value={mode} onChange={e=>setMode(e.target.value)}>
                {Object.entries(PAY_MODES).map(([k,v])=><option key={k} value={k}>{v}</option>)}
              </select></div>
            <div><label style={S.label}>Reference / UTR</label><input style={S.input} value={ref} onChange={e=>setRef(e.target.value)}/></div>
          </div>
          <div>
            <label style={S.label}>Against invoice (optional)</label>
            <select style={S.input} value={invoiceId} onChange={e=>setInvoiceId(e.target.value)} disabled={!supplierId}>
              <option value="">On account — not linked to an invoice</option>
              {open.map(r => <option key={r.id} value={r.id}>{r.invoice_number} · {fmtDMY(r.invoice_date)} · balance {fmt(r.balance)}</option>)}
            </select>
            <div style={{fontSize:11,color:"var(--ink3)",marginTop:4}}>Payments on account are applied to the oldest unpaid invoices automatically.</div>
          </div>
          <div><label style={S.label}>Notes</label><input style={S.input} value={notes} onChange={e=>setNotes(e.target.value)}/></div>
          {error && <div style={{color:"var(--danger)",fontSize:12}}>{error}</div>}
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":isEdit?"Save changes":"Record payment"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── GST IMPORT MODAL ──────────────────────────────────────────
function ImportModal({ existing, suppliers, onClose, onDone }) {
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed]   = useState(null);

  const existingKeys = useMemo(() => new Set(existing.map(i => `${i.suppliers?.gstin}|${i.invoice_number}|${i.doc_type}`)), [existing]);
  const knownGstins = useMemo(() => new Set(suppliers.map(s => s.gstin).filter(Boolean)), [suppliers]);

  const onFile = async (file) => {
    if (!file) return;
    setError(""); setParsed(null); setFileName(file.name); setBusy(true);
    try { setParsed(await parseGstFile(file)); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const fresh = parsed ? parsed.docs.filter(d => !existingKeys.has(`${d.gstin}|${d.invoice_number}|${d.doc_type}`)) : [];
  const dupes = parsed ? parsed.docs.length - fresh.length : 0;
  const newSuppliers = [...new Set(fresh.map(d => d.gstin))].filter(g => !knownGstins.has(g));
  const sum = (arr, t) => arr.filter(d => d.doc_type === t).reduce((s,d) => s + d.invoice_value, 0);
  const bad = fresh.filter(d => !d.invoice_date);

  const run = async () => {
    setBusy(true); setError("");
    try {
      const toAdd = fresh.filter(d => d.invoice_date);
      const idByGstin = {};
      suppliers.forEach(s => { if (s.gstin) idByGstin[s.gstin] = s.id; });
      const newOnes = [...new Map(toAdd.filter(d => !idByGstin[d.gstin]).map(d => [d.gstin, d.name])).entries()];
      if (newOnes.length) {
        const { data, error: se } = await supabase.from("suppliers").insert(newOnes.map(([gstin,name]) => ({ gstin, name }))).select("id,gstin");
        if (se) throw se;
        data.forEach(s => { idByGstin[s.gstin] = s.id; });
      }
      for (let i = 0; i < toAdd.length; i += 200) {
        const chunk = toAdd.slice(i, i + 200).map(({ gstin, name, ...d }) => ({ ...d, supplier_id: idByGstin[gstin] }));
        const { error: ie } = await supabase.from("purchase_invoices").insert(chunk);
        if (ie) throw ie;
      }
      onDone(toAdd.length, newOnes.length);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&!busy&&onClose()}>
      <div style={{...S.modal,maxWidth:560}}>
        <div style={{padding:"22px 26px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:"var(--serif)",fontSize:20,fontWeight:300}}>Import from GST portal</div>
          <button onClick={onClose} disabled={busy} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--ink3)"}}>✕</button>
        </div>
        <div style={{padding:"16px 26px 26px",display:"grid",gap:14}}>
          <div style={{fontSize:12,color:"var(--ink3)"}}>
            Choose the GSTR-2A / 2B Excel downloaded from the GST portal (one month per file). Invoices and credit/debit notes are
            added; anything already in the system is skipped, so re-importing a file is safe.
          </div>
          <input type="file" accept=".xlsx,.xls" onChange={e=>onFile(e.target.files[0])} disabled={busy}/>
          {busy && !parsed && <div style={{fontSize:12,color:"var(--ink3)"}}>Reading {fileName}…</div>}
          {error && <div style={{color:"var(--danger)",fontSize:12}}>{error}</div>}
          {parsed && (
            <div style={{border:"1px solid var(--paper3)",borderRadius:10,padding:"14px 16px",fontSize:13,display:"grid",gap:6}}>
              <div>Tax period <b>{parsed.taxPeriod ? `${parsed.taxPeriod.slice(0,2)}/${parsed.taxPeriod.slice(2)}` : "—"}</b> · {parsed.docs.length} documents in file</div>
              <div>New invoices: <b>{fresh.filter(d=>d.doc_type==="invoice").length}</b> · {fmt(sum(fresh,"invoice"))}</div>
              <div>New credit notes: <b>{fresh.filter(d=>d.doc_type==="credit_note").length}</b> · {fmt(sum(fresh,"credit_note"))}</div>
              {fresh.some(d=>d.doc_type==="debit_note") && <div>New debit notes: <b>{fresh.filter(d=>d.doc_type==="debit_note").length}</b> · {fmt(sum(fresh,"debit_note"))}</div>}
              <div>Already in the system (skipped): <b>{dupes}</b></div>
              <div>New suppliers to create: <b>{newSuppliers.length}</b></div>
              {bad.length > 0 && <div style={{color:"var(--danger)"}}>{bad.length} document(s) have an unreadable date and will be skipped.</div>}
              {parsed.amendments > 0 && <div style={{color:"var(--warn)"}}>⚠ The file has {parsed.amendments} amendment row(s) (B2BA/CDNRA) — these are not imported; check them against the GST portal.</div>}
            </div>
          )}
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button style={S.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
            <button style={S.btnPrimary} onClick={run} disabled={busy || !parsed || fresh.length - bad.length === 0}>
              {busy && parsed ? "Importing…" : `Import ${parsed ? fresh.length - bad.length : ""} documents`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── BANK STATEMENT MATCHING ───────────────────────────────────
// Reuses the debits already stored by Payments > Upload bank statement
// (bank_statement_entries). A debit becomes a supplier payment, or is linked
// to a payment recorded earlier, or is marked "not a purchase".
const BANK_LABELS = { kotak:"Kotak Bank", icici:"ICICI Bank", sbi:"SBI Bank", kbl:"Karnataka Bank", sbi_old:"SBI Bank (old a/c)", credit_card:"Credit Card", gpay:"GPay", phonepay:"Phone Pay" };
const bankLabel = (m) => BANK_LABELS[m] || m;

// Suppliers whose name words appear in the bank narration, best first.
function suggestSuppliers(suppliers, text) {
  const hay = String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, " ");
  return suppliers.map(s => {
    const words = s.name.toLowerCase().replace(/[^a-z0-9]+/g, " ").split(" ").filter(w => w.length >= 4);
    const hits = words.filter(w => hay.includes(w)).length;
    return { s, score: words.length ? hits / words.length : 0, hits };
  }).filter(x => x.hits > 0).sort((a, b) => b.score - a.score || b.hits - a.hits).slice(0, 4).map(x => x.s);
}

function MatchModal({ entry, suppliers, rows, payments, heads, onClose, onDone }) {
  const suggestions = useMemo(() => suggestSuppliers(suppliers, `${entry.description || ""} ${entry.raw_ref || ""}`), [suppliers, entry]);
  const [supplierId, setSupplierId] = useState(suggestions[0]?.id || "");
  const [invoiceId, setInvoiceId]   = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const amt = num(entry.amount);

  const open = rows.filter(r => r.supplier_id === supplierId && r.doc_type !== "credit_note" && r.balance > 0.005)
    .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date));
  useEffect(() => {
    const exact = open.find(r => Math.abs(r.balance - amt) < 0.01);
    setInvoiceId(exact ? exact.id : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const day = (d) => new Date(d).getTime() / 86400000;
  const candidates = payments.filter(p => !p.bank_txn_id && Math.abs(num(p.amount) - amt) < 0.5 && Math.abs(day(p.payment_date) - day(entry.entry_date)) <= 15);

  const run = async (fn) => {
    setSaving(true); setError("");
    const { error: e } = await fn();
    setSaving(false);
    if (e) return setError(/bank_txn_uniq|duplicate/i.test(e.message) ? "This bank debit is already linked to a payment." : e.message);
    onDone();
  };
  const createPayment = () => {
    if (!supplierId) return setError("Pick the supplier this payment was made to.");
    run(() => supabase.from("purchase_payments").insert({
      supplier_id: supplierId, purchase_invoice_id: invoiceId || null, payment_date: entry.entry_date, amount: amt,
      mode: "bank", reference_no: entry.raw_ref || null, notes: `Bank: ${bankLabel(entry.payment_mode)}`, bank_txn_id: entry.id }));
  };
  const link = (p) => run(() => supabase.from("purchase_payments").update({ bank_txn_id: entry.id }).eq("id", p.id));
  const categorize = (h) => run(() => supabase.from("bank_statement_entries").update({ head_id: h.id }).eq("id", entry.id));

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{...S.modal,maxWidth:560}}>
        <div style={{padding:"22px 26px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontFamily:"var(--serif)",fontSize:20,fontWeight:300}}>Match bank debit — {fmt(amt)}</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>{fmtDMY(entry.entry_date)} · {bankLabel(entry.payment_mode)}{entry.raw_ref ? ` · ${entry.raw_ref}` : ""}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--ink3)"}}>✕</button>
        </div>
        <div style={{padding:"16px 26px 26px",display:"grid",gap:14}}>
          <div style={{fontSize:12,background:"var(--paper2)",borderRadius:8,padding:"10px 12px",wordBreak:"break-word"}}>{entry.description || "(no description)"}</div>

          {candidates.length > 0 && (
            <div>
              <label style={S.label}>Payments already recorded for this amount</label>
              {candidates.map(p => (
                <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:6,fontSize:12}}>
                  <span>{suppliers.find(x=>x.id===p.supplier_id)?.name} · {fmtDMY(p.payment_date)}{p.reference_no ? ` · ${p.reference_no}` : ""}</span>
                  <button style={S.btnSm} disabled={saving} onClick={()=>link(p)}>Link</button>
                </div>
              ))}
            </div>
          )}

          <div style={{borderTop:candidates.length?"1px solid var(--paper2)":"none",paddingTop:candidates.length?14:0,display:"grid",gap:12}}>
            <label style={{...S.label,marginBottom:0}}>Or record it as a new supplier payment</label>
            {suggestions.length > 0 && (
              <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",fontSize:11,color:"var(--ink3)"}}>Looks like:
                {suggestions.map(x => <button key={x.id} style={{...S.pill(supplierId===x.id,"var(--accent)"),borderRadius:10,textAlign:"left",lineHeight:1.3}} onClick={()=>setSupplierId(x.id)}>{x.name}<div style={{fontSize:10,opacity:0.85}}>{x.gstin || "no GSTIN"}</div></button>)}
              </div>
            )}
            <SupplierPicker key={supplierId || "none"} suppliers={suppliers} value={supplierId} onChange={setSupplierId} label="Supplier"/>
            <div>
              <label style={S.label}>Against invoice (optional)</label>
              <select style={S.input} value={invoiceId} onChange={e=>setInvoiceId(e.target.value)} disabled={!supplierId}>
                <option value="">On account — not linked to an invoice</option>
                {open.map(r => <option key={r.id} value={r.id}>{r.invoice_number} · {fmtDMY(r.invoice_date)} · balance {fmt(r.balance)}</option>)}
              </select>
            </div>
          </div>
          <div style={{borderTop:"1px solid var(--paper2)",paddingTop:14}}>
            <label style={S.label}>Not a purchase? File it under a head</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {heads.map(h => <button key={h.id} style={S.btnSm} disabled={saving} onClick={()=>categorize(h)}>{h.name}</button>)}
            </div>
          </div>
          {error && <div style={{color:"var(--danger)",fontSize:12}}>{error}</div>}
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={createPayment} disabled={saving}>{saving ? "Saving…" : "Record payment"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeadsModal({ heads, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const run = async (fn) => { setError(""); const { error: e } = await fn(); if (e) return setError(/expense_heads_name_uniq|duplicate/i.test(e.message) ? "A head with that name already exists." : e.message); onChanged(); };
  const add = async () => {
    if (!name.trim()) return;
    await run(() => supabase.from("expense_heads").insert({ name: name.trim(), sort_order: 50 }));
    setName("");
  };
  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{...S.modal,maxWidth:440}}>
        <div style={{padding:"22px 26px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:"var(--serif)",fontSize:20,fontWeight:300}}>Expense heads</div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--ink3)"}}>✕</button>
        </div>
        <div style={{padding:"16px 26px 26px",display:"grid",gap:10}}>
          {heads.map(h => (
            <div key={h.id} style={{display:"flex",gap:8,alignItems:"center"}}>
              <input style={S.input} defaultValue={h.name}
                onBlur={e => { const v = e.target.value.trim(); if (v && v !== h.name) run(() => supabase.from("expense_heads").update({ name: v }).eq("id", h.id)); }}/>
              <button style={S.btnSm} title="Hide this head from the lists (existing entries keep it)" onClick={()=>run(() => supabase.from("expense_heads").update({ is_active:false }).eq("id", h.id))}>Hide</button>
            </div>
          ))}
          <div style={{display:"flex",gap:8,marginTop:6}}>
            <input style={S.input} placeholder="New head, e.g. Rent" value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()}/>
            <button style={S.btnPrimary} onClick={add}>Add</button>
          </div>
          {error && <div style={{color:"var(--danger)",fontSize:12}}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

// Which months each bank account has statement entries for; months with
// nothing between the first and last entry are flagged as gaps.
function CoveragePanel() {
  const [data, setData]   = useState(null);
  const [error, setError] = useState("");
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  useEffect(() => {
    fetchAllRows(() => supabase.from("bank_statement_entries").select("payment_mode,entry_date,entry_type").order("id"))
      .then(setData).catch(e => setError(e.message));
  }, []);

  if (error) return <div style={{color:"var(--danger)",fontSize:12,marginBottom:14}}>{error}</div>;
  if (!data) return <div style={{fontSize:12,color:"var(--ink3)",marginBottom:14}}>Reading uploaded statements…</div>;
  if (data.length === 0) return <div style={{fontSize:12,color:"var(--ink3)",marginBottom:14}}>No statements uploaded yet.</div>;

  const byAcct = {};
  data.forEach(e => {
    if (!e.entry_date) return;
    const a = (byAcct[e.payment_mode] = byAcct[e.payment_mode] || { months:{}, first:e.entry_date, last:e.entry_date, n:0 });
    const ym = e.entry_date.slice(0, 7);
    const m = (a.months[ym] = a.months[ym] || { credit:0, debit:0 });
    m[e.entry_type === "debit" ? "debit" : "credit"]++;
    a.n++;
    if (e.entry_date < a.first) a.first = e.entry_date;
    if (e.entry_date > a.last) a.last = e.entry_date;
  });
  const monthsBetween = (from, to) => {
    const out = []; let [y, m] = from.slice(0, 7).split("-").map(Number);
    const [ey, em] = to.slice(0, 7).split("-").map(Number);
    while (y < ey || (y === ey && m <= em)) { out.push(`${y}-${String(m).padStart(2, "0")}`); m++; if (m > 12) { m = 1; y++; } }
    return out;
  };

  return (
    <div style={{...S.card,padding:"14px 18px",marginBottom:16,display:"grid",gap:16}}>
      {Object.entries(byAcct).sort().map(([acct, a]) => {
        const months = monthsBetween(a.first, a.last);
        const gaps = months.filter(ym => !a.months[ym]).length;
        return (
          <div key={acct}>
            <div style={{display:"flex",gap:12,alignItems:"baseline",flexWrap:"wrap",marginBottom:8}}>
              <b style={{fontSize:13}}>{bankLabel(acct)}</b>
              <span style={{fontSize:12,color:"var(--ink3)"}}>{fmtDMY(a.first)} to {fmtDMY(a.last)} · {a.n} entries</span>
              {gaps > 0 && <span style={{fontSize:12,color:"var(--danger)"}}>{gaps} month{gaps>1?"s":""} with no entries</span>}
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {months.map(ym => {
                const m = a.months[ym];
                const [y, mo] = ym.split("-");
                return (
                  <div key={ym} title={m ? `${m.credit} credits, ${m.debit} debits` : "No entries for this month"}
                    style={{width:74,padding:"6px 8px",borderRadius:8,fontSize:11,fontFamily:"var(--mono)",textAlign:"center",
                      border:`1px solid ${m ? "var(--paper3)" : "var(--danger)"}`,background:m ? "#fff" : "var(--danger-l)",color:m ? "var(--ink2)" : "var(--danger)"}}>
                    <div style={{fontWeight:500}}>{MONTHS[Number(mo)-1]} {y.slice(2)}</div>
                    <div style={{fontSize:10}}>{m ? `${m.credit}cr · ${m.debit}dr` : "missing"}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Records every selected bank debit as an on-account payment to one supplier,
// so a run of statement lines (say, all the Vairam transfers) is done in one go.
function BulkSupplierModal({ entries, suppliers, onClose, onDone }) {
  const [supplierId, setSupplierId] = useState(() =>
    suggestSuppliers(suppliers, `${entries[0]?.description || ""} ${entries[0]?.raw_ref || ""}`)[0]?.id || "");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const total = entries.reduce((a, e) => a + num(e.amount), 0);
  const save = async () => {
    if (!supplierId) return setError("Pick the supplier these payments were made to.");
    setSaving(true); setError("");
    const payload = entries.map(e => ({
      supplier_id: supplierId, purchase_invoice_id: null, payment_date: e.entry_date, amount: num(e.amount),
      mode: "bank", reference_no: e.raw_ref || null, notes: `Bank: ${bankLabel(e.payment_mode)}`, bank_txn_id: e.id }));
    for (let i = 0; i < payload.length; i += 200) {
      const { error: err } = await supabase.from("purchase_payments").insert(payload.slice(i, i + 200));
      if (err) {
        setSaving(false);
        return setError((/bank_txn_uniq|duplicate/i.test(err.message) ? "One of these debits is already linked to a payment. " : "") + err.message + (i ? ` (${i} saved before this)` : ""));
      }
    }
    onDone(payload.length, suppliers.find(x => x.id === supplierId)?.name);
  };
  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:480,padding:"24px 28px"}}>
        <div style={S.pageTitle}>Assign to supplier</div>
        <div style={{fontSize:13,margin:"12px 0 16px",color:"var(--ink2)"}}>
          <b>{entries.length}</b> bank debit{entries.length > 1 ? "s" : ""} totalling <b>{fmt(total)}</b> will be recorded as payments to this supplier.
          They go on the supplier's ledger as on-account payments and count against the oldest unpaid invoices first.
        </div>
        <SupplierPicker suppliers={suppliers} value={supplierId} onChange={setSupplierId} label="Supplier"/>
        {error && <div style={{color:"var(--danger)",fontSize:12,marginTop:12}}>{error}</div>}
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20}}>
          <button style={S.btnGhost} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} disabled={saving || !supplierId} onClick={save}>{saving ? "Saving…" : `Record ${entries.length} payment${entries.length > 1 ? "s" : ""}`}</button>
        </div>
      </div>
    </div>
  );
}

// Heads only the "admin" login may see. Everyone else gets these heads, and the
// bank debits filed under them, filtered out of the Bank tab entirely.
const PRIVATE_HEADS = ["salary", "loan"];
const OWNER_USERNAME = "admin";

function BankTab({ suppliers, rows, payments, fromDate, toDate, onChanged, toast }) {
  const { user } = useAuth();
  const isOwner = user?.username === OWNER_USERNAME;
  const [rawEntries, setEntries] = useState([]);
  const [hiddenHeads, setHiddenHeads] = useState(new Set());
  const [headsLoaded, setHeadsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [acct, setAcct]       = useState("all");
  const [state, setState]     = useState("unmatched");
  const [text, setText]       = useState("");
  const [page, setPage]       = useState(0);
  const [matching, setMatching] = useState(null);
  const [showCoverage, setShowCoverage] = useState(false);
  const [heads, setHeads]     = useState([]);
  const [allHeads, setAllHeads] = useState([]);
  const [headFilter, setHeadFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [bulkHead, setBulkHead] = useState("");
  const [bulkSupplier, setBulkSupplier] = useState(false);
  const [headsOpen, setHeadsOpen] = useState(false);
  const [showTotals, setShowTotals] = useState(false);
  const PAGE_SIZE = 100;

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      setEntries(await fetchAllRows(() => {
        let q = supabase.from("bank_statement_entries").select("*").eq("entry_type", "debit");
        if (fromDate) q = q.gte("entry_date", fromDate);
        if (toDate) q = q.lte("entry_date", toDate);
        return q.order("entry_date", { ascending:false }).order("id");
      }));
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [acct, state, text, fromDate, toDate, headFilter]);
  useEffect(() => { setSelected(new Set()); }, [acct, state, text, fromDate, toDate, headFilter]);

  const loadHeads = useCallback(async () => {
    const { data, error: e } = await supabase.from("expense_heads").select("*").is("parent_id", null).order("sort_order").order("name");
    if (e) return setError(/expense_heads|schema cache|does not exist/i.test(e.message) ? "Run sql/expense_heads.sql in Supabase first." : e.message);
    const hidden = new Set(isOwner ? [] : (data || []).filter(h => PRIVATE_HEADS.includes(h.name.trim().toLowerCase())).map(h => h.id));
    setHiddenHeads(hidden); setHeadsLoaded(true);
    const visible = (data || []).filter(h => !hidden.has(h.id));
    setAllHeads(visible); setHeads(visible.filter(h => h.is_active));
  }, [isOwner]);
  useEffect(() => { loadHeads(); }, [loadHeads]);

  // Until the heads have loaded nothing is shown to non-owners, so private
  // entries never flash up.
  const headsReady = isOwner || headsLoaded;
  const entries = useMemo(() => !headsReady ? [] : rawEntries.filter(e => !hiddenHeads.has(e.head_id)), [rawEntries, hiddenHeads, headsReady]);

  const payByEntry = useMemo(() => {
    const m = {};
    payments.forEach(p => { if (p.bank_txn_id) m[p.bank_txn_id] = p; });
    return m;
  }, [payments]);

  const headName = useMemo(() => { const m = {}; allHeads.forEach(h => { m[h.id] = h.name; }); return m; }, [allHeads]);
  const withState = entries.map(e => ({ ...e, st: payByEntry[e.id] ? "matched" : e.head_id ? "categorized" : "unmatched" }));
  const accts = [...new Set(entries.map(e => e.payment_mode))].sort();
  const t = text.trim().toLowerCase();
  const shown = withState.filter(e => (acct === "all" || e.payment_mode === acct) && (state === "all" || e.st === state) &&
    (headFilter === "all" || e.head_id === headFilter) &&
    (!t || (e.description || "").toLowerCase().includes(t) || (e.raw_ref || "").toLowerCase().includes(t) || String(e.amount).includes(t)));
  const unmatched = withState.filter(e => e.st === "unmatched" && (acct === "all" || e.payment_mode === acct));
  const pageRows = shown.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const byHead = {};
  withState.filter(e => e.st === "categorized" && (acct === "all" || e.payment_mode === acct)).forEach(e => {
    const h = (byHead[e.head_id] = byHead[e.head_id] || { n:0, sum:0 }); h.n++; h.sum += num(e.amount);
  });
  const selectable = shown.filter(e => e.st !== "matched");
  const selSum = selectable.filter(e => selected.has(e.id)).reduce((a, e) => a + num(e.amount), 0);
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const applyBulk = async () => {
    const ids = selectable.filter(e => selected.has(e.id)).map(e => e.id);
    if (!bulkHead || ids.length === 0) return;
    for (let i = 0; i < ids.length; i += 200) {
      const { error: e } = await supabase.from("bank_statement_entries").update({ head_id: bulkHead === "none" ? null : bulkHead }).in("id", ids.slice(i, i + 200));
      if (e) { toast("Error: " + e.message, "error"); await load(); return; }
    }
    toast(`${ids.length} debit${ids.length > 1 ? "s" : ""} updated.`);
    setSelected(new Set()); setBulkHead(""); await load();
  };

  const act = async (fn, msg) => {
    const { error: e } = await fn();
    if (e) return toast("Error: " + e.message, "error");
    toast(msg); await load(); onChanged();
  };
  const stCfg = { unmatched:["Unmatched","var(--danger)"], matched:["Purchase payment","var(--accent)"], categorized:["Categorized","var(--info)"] };

  return (
    <div>
      {error && <div style={{background:"var(--warn-l)",color:"var(--warn)",padding:"12px 16px",borderRadius:8,fontSize:13,marginBottom:14}}>{error}</div>}
      <div style={{fontSize:12,color:"var(--ink3)",marginBottom:12}}>
        Debits from the bank statements uploaded in Payments. Upload a statement there once — it is used for both sales and purchases.
        {" "}<button style={S.btnSm} onClick={()=>setShowCoverage(v=>!v)}>📅 {showCoverage ? "Hide" : "Show"} uploaded statements</button>
      </div>
      {showCoverage && <CoveragePanel/>}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        <select style={{...S.input,width:170}} value={acct} onChange={e=>setAcct(e.target.value)}>
          <option value="all">All accounts</option>
          {accts.map(a => <option key={a} value={a}>{bankLabel(a)}</option>)}
        </select>
        {[["unmatched","Unmatched","var(--danger)"],["matched","Purchase payments","var(--accent)"],["categorized","Categorized","var(--info)"],["all","All","var(--ink2)"]].map(([k,l,c]) =>
          <button key={k} style={S.pill(state===k,c)} onClick={()=>{ setState(k); if (k !== "categorized") setHeadFilter("all"); }}>{l}</button>)}
        <select style={{...S.input,width:150}} value={headFilter} onChange={e=>{ setHeadFilter(e.target.value); if (e.target.value !== "all") setState("categorized"); }}>
          <option value="all">All heads</option>
          {allHeads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <button style={S.btnSm} onClick={()=>setHeadsOpen(true)}>⚙ Heads</button>
        <button style={S.btnSm} onClick={()=>setShowTotals(v=>!v)}>📊 {showTotals ? "Hide" : "Show"} totals by head</button>
        <input style={{...S.input,maxWidth:240}} placeholder="🔍 Description, ref or amount" value={text} onChange={e=>setText(e.target.value)}/>
        <div style={{marginLeft:"auto",fontFamily:"var(--mono)",fontSize:12}}>
          Unmatched <b>{unmatched.length}</b> · <b>{fmt(unmatched.reduce((a, e) => a + num(e.amount), 0))}</b>
        </div>
      </div>
      {showTotals && Object.keys(byHead).length === 0 && <div style={{fontSize:12,color:"var(--ink3)",marginBottom:14}}>Nothing categorized yet.</div>}
      {showTotals && Object.keys(byHead).length > 0 && (
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
          {Object.entries(byHead).sort((a, b) => b[1].sum - a[1].sum).map(([id, h]) => (
            <button key={id} style={{...S.pill(headFilter===id,"var(--info)"),borderRadius:10,textAlign:"left",lineHeight:1.3}}
              onClick={()=>{ if (headFilter === id) { setHeadFilter("all"); } else { setHeadFilter(id); setState("categorized"); } }}>
              {headName[id] || "?"}<div style={{fontSize:10,opacity:0.85}}>{fmt(h.sum)} · {h.n}</div>
            </button>
          ))}
        </div>
      )}
      {selected.size > 0 && (
        <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:12,padding:"10px 14px",background:"var(--paper2)",borderRadius:10,flexWrap:"wrap",fontSize:12}}>
          <b>{selected.size} selected · {fmt(selSum)}</b>
          <select style={{...S.input,width:200}} value={bulkHead} onChange={e=>setBulkHead(e.target.value)}>
            <option value="">Move to head…</option>
            {heads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
            <option value="none">— Clear head (back to unmatched) —</option>
          </select>
          <button style={S.btnPrimary} disabled={!bulkHead} onClick={applyBulk}>Apply</button>
          <button style={S.btnPrimary} onClick={()=>setBulkSupplier(true)}>Assign to supplier…</button>
          <button style={S.btnSm} onClick={()=>setSelected(new Set())}>Clear selection</button>
        </div>
      )}
      <div style={S.card}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"var(--paper2)"}}>
              <th style={{...S.th(),width:30}}><input type="checkbox" title={`Select all ${selectable.length} shown`} checked={selectable.length > 0 && selected.size === selectable.length}
                onChange={e=>setSelected(e.target.checked ? new Set(selectable.map(x => x.id)) : new Set())}/></th>
              <th style={S.th()}>Date</th><th style={S.th()}>Account</th><th style={S.th()}>Description</th>
              <th style={S.th(true)}>Amount</th><th style={S.th()}>Status</th><th style={S.th()}></th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{...S.td,textAlign:"center",color:"var(--ink3)",padding:30}}>Loading…</td></tr>}
              {!loading && shown.length === 0 && <tr><td colSpan={7} style={{...S.td,textAlign:"center",color:"var(--ink3)",padding:30}}>{entries.length === 0 ? "No bank debits found — upload a statement in Payments." : "Nothing matches the filters."}</td></tr>}
              {pageRows.map(e => {
                const pay = payByEntry[e.id];
                return (
                  <tr key={e.id} style={{borderTop:"1px solid var(--paper2)",background:selected.has(e.id)?"var(--paper2)":undefined}}>
                    <td style={S.td}>{e.st !== "matched" && <input type="checkbox" checked={selected.has(e.id)} onChange={()=>toggle(e.id)}/>}</td>
                    <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{fmtDMY(e.entry_date)}</td>
                    <td style={{...S.td,fontSize:12}}>{bankLabel(e.payment_mode)}</td>
                    <td style={{...S.td,fontSize:12,maxWidth:380,wordBreak:"break-word"}}>{e.description || "—"}{e.raw_ref ? <div style={{fontSize:10,color:"var(--ink3)"}}>{e.raw_ref}</div> : null}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{fmt(e.amount)}</td>
                    <td style={S.td}><span style={{color:stCfg[e.st][1],fontSize:12,fontWeight:500}}>{e.st === "categorized" ? (headName[e.head_id] || "Categorized") : stCfg[e.st][0]}</span>
                      {pay && <div style={{fontSize:10,color:"var(--ink3)"}}>{suppliers.find(x=>x.id===pay.supplier_id)?.name}</div>}</td>
                    <td style={{...S.td,whiteSpace:"nowrap"}}>
                      {e.st === "unmatched" && <button style={S.btnSm} onClick={()=>setMatching(e)}>Match</button>}
                      {e.st === "matched" && <button style={S.btnSm} onClick={()=>act(()=>supabase.from("purchase_payments").update({ bank_txn_id:null }).eq("id", pay.id), "Unmatched.")}>Unmatch</button>}
                      {e.st === "categorized" && <button style={S.btnSm} onClick={()=>act(()=>supabase.from("bank_statement_entries").update({ head_id:null }).eq("id", e.id), "Moved back to unmatched.")}>Clear</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {shown.length > PAGE_SIZE && (
        <div style={{display:"flex",gap:10,alignItems:"center",justifyContent:"center",marginTop:14,fontSize:12}}>
          <button style={S.btnSm} disabled={page===0} onClick={()=>setPage(p=>p-1)}>← Prev</button>
          <span>{page*PAGE_SIZE+1}–{Math.min((page+1)*PAGE_SIZE, shown.length)} of {shown.length}</span>
          <button style={S.btnSm} disabled={(page+1)*PAGE_SIZE>=shown.length} onClick={()=>setPage(p=>p+1)}>Next →</button>
        </div>
      )}
      {bulkSupplier && <BulkSupplierModal suppliers={suppliers} entries={selectable.filter(e => selected.has(e.id))} onClose={()=>setBulkSupplier(false)}
        onDone={async (n, name) => { setBulkSupplier(false); setSelected(new Set()); toast(`${n} payment${n > 1 ? "s" : ""} recorded for ${name}.`); await load(); onChanged(); }}/>}
      {headsOpen && <HeadsModal heads={allHeads.filter(h => h.is_active)} onClose={()=>setHeadsOpen(false)} onChanged={loadHeads}/>}
      {matching && <MatchModal entry={matching} suppliers={suppliers} rows={rows} payments={payments} heads={heads}
        onClose={()=>setMatching(null)} onDone={async ()=>{ setMatching(null); toast("Saved."); await load(); onChanged(); }}/>}
    </div>
  );
}

// ── PURCHASES PAGE ────────────────────────────────────────────
export function PurchasesPage({ toast }) {
  const [invoices, setInvoices]   = useState([]);
  const [payments, setPayments]   = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState("");
  const [view, setView]           = useState("invoices");
  const [search, setSearch]       = useState("");
  const [fromDate, setFromDate]   = useState("");
  const [toDate, setToDate]       = useState("");
  const [status, setStatus]       = useState("all");
  const [page, setPage]           = useState(0);
  const [invModal, setInvModal]   = useState(null);   // true | record
  const [payModal, setPayModal]   = useState(null);   // { invoice?, supplierId?, record? }
  const [ledgerSupplier, setLedgerSupplier] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const PAGE_SIZE = 100;

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const [inv, pay, sup] = await Promise.all([
        fetchAllRows(() => supabase.from("purchase_invoices").select("*, suppliers(id,name,gstin)").eq("is_deleted", false).order("invoice_date", { ascending:false }).order("id")),
        fetchAllRows(() => supabase.from("purchase_payments").select("*").eq("is_deleted", false).order("payment_date").order("id")),
        fetchAllRows(() => supabase.from("suppliers").select("*").eq("is_active", true).order("name").order("id")),
      ]);
      setInvoices(inv); setPayments(pay); setSuppliers(sup);
    } catch (e) {
      setLoadError(/relation|does not exist|schema cache/i.test(e.message) ? "The purchase tables aren't set up yet — run sql/purchases.sql in Supabase first." : e.message);
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const paymentsByInvoice = useMemo(() => {
    const m = {};
    payments.forEach(p => { if (p.purchase_invoice_id) (m[p.purchase_invoice_id] = m[p.purchase_invoice_id] || []).push(p); });
    return m;
  }, [payments]);

  // Payments linked to an invoice reduce it directly. Payments on account (and
  // credit notes) are applied to that supplier's oldest unpaid invoices first.
  const rows = useMemo(() => {
    const pool = {};
    payments.forEach(p => { if (!p.purchase_invoice_id) pool[p.supplier_id] = (pool[p.supplier_id] || 0) + num(p.amount); });
    invoices.forEach(i => { if (i.doc_type === "credit_note") pool[i.supplier_id] = (pool[i.supplier_id] || 0) + num(i.invoice_value); });
    const alloc = {};
    [...invoices].filter(i => i.doc_type !== "credit_note")
      .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date) || a.created_at.localeCompare(b.created_at))
      .forEach(i => {
        const linked = (paymentsByInvoice[i.id] || []).reduce((t, p) => t + num(p.amount), 0);
        const take = Math.max(0, Math.min(pool[i.supplier_id] || 0, num(i.invoice_value) - linked));
        pool[i.supplier_id] = (pool[i.supplier_id] || 0) - take;
        alloc[i.id] = { linked, take };
      });
    return invoices.map(i => {
      const credit = i.doc_type === "credit_note";
      const al = alloc[i.id] || { linked: 0, take: 0 };
      const paid = round2(al.linked + al.take);
      const balance = credit ? 0 : round2(num(i.invoice_value) - paid);
      const st = credit ? "credit" : balance <= 0.005 ? "paid" : paid > 0 ? "part" : "unpaid";
      return { ...i, paid, balance, st };
    });
  }, [invoices, payments, paymentsByInvoice]);

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(r =>
    (!q || r.suppliers?.name?.toLowerCase().includes(q) || r.suppliers?.gstin?.toLowerCase().includes(q) || r.invoice_number.toLowerCase().includes(q)) &&
    (!fromDate || r.invoice_date >= fromDate) && (!toDate || r.invoice_date <= toDate) &&
    (status === "all" || r.st === status));

  useEffect(() => { setPage(0); }, [search, fromDate, toDate, status, view]);

  const signed = (r) => r.doc_type === "credit_note" ? -num(r.invoice_value) : num(r.invoice_value);
  const totals = filtered.reduce((t, r) => ({ billed: t.billed + signed(r), paid: t.paid + r.paid, bal: t.bal + r.balance }), { billed:0, paid:0, bal:0 });

  const supplierRows = useMemo(() => {
    const m = {};
    suppliers.forEach(s => { m[s.id] = { ...s, count:0, billed:0, paid:0 }; });
    rows.forEach(r => { const s = m[r.supplier_id]; if (s) { s.count++; s.billed += signed(r); } });
    payments.forEach(p => { const s = m[p.supplier_id]; if (s) s.paid += num(p.amount); });
    return Object.values(m).map(s => ({ ...s, balance: round2(s.billed - s.paid) }))
      .filter(s => !q || s.name.toLowerCase().includes(q) || (s.gstin||"").toLowerCase().includes(q));
  }, [suppliers, rows, payments, q]);

  // Running ledger for one supplier: documents add to what we owe, payments reduce it.
  const ledger = useMemo(() => {
    if (!ledgerSupplier) return null;
    const entries = [];
    rows.filter(r => r.supplier_id === ledgerSupplier).forEach(r => entries.push({
      key:"i"+r.id, date:r.invoice_date, created:r.created_at, order:0, inv:r,
      label:`${DOC_LABELS[r.doc_type]} ${r.invoice_number}`, billed: r.doc_type === "credit_note" ? -num(r.invoice_value) : num(r.invoice_value), paid:0 }));
    payments.filter(p => p.supplier_id === ledgerSupplier).forEach(p => {
      const linked = rows.find(r => r.id === p.purchase_invoice_id);
      entries.push({ key:"p"+p.id, date:p.payment_date, created:p.created_at, order:1, pay:p,
        label:`Payment · ${PAY_MODES[p.mode]||p.mode}${p.reference_no ? ` · ${p.reference_no}` : ""}${linked ? ` · against ${linked.invoice_number}` : ""}`, billed:0, paid:num(p.amount) });
    });
    entries.sort((a,b) => a.date.localeCompare(b.date) || a.order - b.order || (a.created||"").localeCompare(b.created||""));
    let bal = 0;
    entries.forEach(e => { bal = round2(bal + e.billed - e.paid); e.balance = bal; });
    const inRange = entries.filter(e => (!fromDate || e.date >= fromDate) && (!toDate || e.date <= toDate));
    const opening = fromDate ? (entries.filter(e => e.date < fromDate).slice(-1)[0]?.balance || 0) : 0;
    return { entries: inRange, opening, closing: entries.length ? entries[entries.length-1].balance : 0 };
  }, [ledgerSupplier, rows, payments, fromDate, toDate]);

  const removePayment = async (p) => {
    if (!window.confirm(`Delete this payment of ${fmt(p.amount)} on ${fmtDMY(p.payment_date)}?`)) return;
    const { error } = await supabase.from("purchase_payments").update({ is_deleted:true }).eq("id", p.id);
    if (error) return toast("Error: " + error.message, "error");
    toast("Payment deleted."); load();
  };

  const downloadLedgerCSV = () => {
    if (!ledger || !ledger.entries.length) return alert("Nothing to download.");
    const sup = suppliers.find(x => x.id === ledgerSupplier);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = [["Supplier", sup?.name, sup?.gstin], [],
      ["Date","Particulars","Billed","Paid","Balance payable"],
      ...(fromDate ? [["", "Opening balance", "", "", ledger.opening]] : []),
      ...ledger.entries.map(e => [fmtDMY(e.date), e.label, e.billed || "", e.paid || "", e.balance])];
    const csv = "\uFEFF" + body.map(r => r.map(esc).join(",")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv;charset=utf-8" }));
    a.download = `ledger-${(sup?.name||"supplier").replace(/[^a-z0-9]+/gi,"-")}-${todayISO()}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${DOC_LABELS[r.doc_type].toLowerCase()} ${r.invoice_number} from ${r.suppliers?.name}?${r.paid ? " Its payments stay on the supplier's account." : ""}`)) return;
    const { error } = await supabase.from("purchase_invoices").update({ is_deleted:true, updated_at:new Date().toISOString() }).eq("id", r.id);
    if (error) return toast("Error: " + error.message, "error");
    toast("Deleted."); load();
  };

  const downloadCSV = () => {
    if (!filtered.length) return alert("Nothing to download.");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Date","Supplier","GSTIN","Type","Number","Taxable","CGST","SGST","IGST","Cess","Total","Paid","Balance","Reverse charge","Tax period"];
    const body = [...filtered].sort((a,b) => a.invoice_date.localeCompare(b.invoice_date)).map(r => [
      fmtDMY(r.invoice_date), r.suppliers?.name, r.suppliers?.gstin, DOC_LABELS[r.doc_type], r.invoice_number,
      r.taxable_value, r.cgst, r.sgst, r.igst, r.cess, signed(r), r.paid, r.balance, r.reverse_charge ? "Y" : "", r.tax_period || "" ]);
    body.push(["TOTAL","","","","","","","","","", round2(totals.billed), round2(totals.paid), round2(totals.bal), "", ""]);
    const csv = "﻿" + [head, ...body].map(r => r.map(esc).join(",")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv;charset=utf-8" }));
    a.download = `purchases-${todayISO()}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };

  const stCfg = { unpaid:["Unpaid","var(--danger)"], part:["Part paid","var(--warn)"], paid:["Paid","var(--accent)"], credit:["Credit note","var(--info)"] };
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div>
      <div style={S.pageHeader}>
        <div>
          <div style={S.pageTitle}>Purchases</div>
          <div style={S.subtitle}>Supplier invoices, GST portal import and payments made</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button style={S.btnGhost} onClick={downloadCSV}>⬇ CSV</button>
          <button style={S.btnGhost} onClick={()=>setImportOpen(true)}>⬆ Import GST file</button>
          <button style={S.btnGhost} onClick={()=>setPayModal({})}>💳 Record payment</button>
          <button style={S.btnPrimary} onClick={()=>setInvModal(true)}>+ Add invoice</button>
        </div>
      </div>

      {loadError && <div style={{background:"var(--warn-l)",color:"var(--warn)",padding:"12px 16px",borderRadius:8,fontSize:13,marginBottom:16}}>{loadError}</div>}

      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        {[["invoices","Invoices"],["suppliers","Suppliers"],["ledger","Ledger"],["bank","Bank"]].map(([k,l]) => <button key={k} style={S.pill(view===k,"var(--accent)")} onClick={()=>setView(k)}>{l}</button>)}
        <input style={{...S.input,maxWidth:300}} placeholder={view==="invoices"?"🔍 Supplier, GSTIN or invoice no.":"🔍 Supplier or GSTIN"} value={search} onChange={e=>setSearch(e.target.value)} hidden={view==="ledger"||view==="bank"}/>
        {view !== "suppliers" && <>
          <input type="date" style={{...S.input,width:150}} value={fromDate} onChange={e=>setFromDate(e.target.value)}/>
          <input type="date" style={{...S.input,width:150}} value={toDate} onChange={e=>setToDate(e.target.value)}/>
          {(fromDate||toDate) && <button style={S.btnSm} onClick={()=>{setFromDate("");setToDate("");}}>Clear</button>}
        </>}
      </div>

      {view === "invoices" && <>
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
          {[["all","All","var(--ink2)"],...Object.entries(stCfg).map(([k,[l,c]])=>[k,l,c])].map(([k,l,c]) =>
            <button key={k} style={S.pill(status===k,c)} onClick={()=>setStatus(k)}>{l}</button>)}
          <div style={{marginLeft:"auto",display:"flex",gap:16,fontFamily:"var(--mono)",fontSize:12}}>
            <span>Billed <b>{fmt(totals.billed)}</b></span><span>Paid <b>{fmt(totals.paid)}</b></span><span>Balance <b>{fmt(totals.bal)}</b></span>
          </div>
        </div>
        <div style={S.card}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--paper2)"}}>
                <th style={S.th()}>Date</th><th style={S.th()}>Supplier</th><th style={S.th()}>Number</th>
                <th style={S.th(true)}>Taxable</th><th style={S.th(true)}>Tax</th><th style={S.th(true)}>Total</th>
                <th style={S.th(true)}>Paid</th><th style={S.th(true)}>Balance</th><th style={S.th()}>Status</th><th style={S.th()}></th>
              </tr></thead>
              <tbody>
                {loading && <tr><td colSpan={10} style={{...S.td,textAlign:"center",color:"var(--ink3)",padding:30}}>Loading…</td></tr>}
                {!loading && filtered.length === 0 && <tr><td colSpan={10} style={{...S.td,textAlign:"center",color:"var(--ink3)",padding:30}}>{rows.length === 0 ? "No purchase invoices yet — import a GST file or add one." : "Nothing matches the filters."}</td></tr>}
                {pageRows.map(r => (
                  <tr key={r.id} style={{borderTop:"1px solid var(--paper2)"}}>
                    <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{fmtDMY(r.invoice_date)}</td>
                    <td style={S.td}>{r.suppliers?.name}<div style={{fontSize:10,color:"var(--ink3)"}}>{r.suppliers?.gstin}</div></td>
                    <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{r.invoice_number}
                      {r.doc_type !== "invoice" && <div style={{fontSize:10,color:"var(--info)"}}>{DOC_LABELS[r.doc_type]}</div>}
                      {r.reverse_charge && <div style={{fontSize:10,color:"var(--ink3)"}}>reverse charge</div>}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(r.taxable_value)}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(num(r.cgst)+num(r.sgst)+num(r.igst)+num(r.cess))}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{r.doc_type==="credit_note"?"−":""}{fmt(r.invoice_value)}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{r.doc_type==="credit_note"?"—":fmt(r.paid)}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{r.doc_type==="credit_note"?"—":fmt(r.balance)}</td>
                    <td style={S.td}><span style={{color:stCfg[r.st][1],fontSize:12,fontWeight:500}}>{stCfg[r.st][0]}</span></td>
                    <td style={{...S.td,whiteSpace:"nowrap"}}>
                      {r.doc_type !== "credit_note" && <button style={S.btnSm} onClick={()=>setPayModal({ invoice:r })}>💳 Pay</button>}{" "}
                      <button style={S.btnSm} onClick={()=>setInvModal(r)}>✎</button>{" "}
                      <button style={S.btnSm} onClick={()=>remove(r)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {filtered.length > PAGE_SIZE && (
          <div style={{display:"flex",gap:10,alignItems:"center",justifyContent:"center",marginTop:14,fontSize:12}}>
            <button style={S.btnSm} disabled={page===0} onClick={()=>setPage(p=>p-1)}>← Prev</button>
            <span>{page*PAGE_SIZE+1}–{Math.min((page+1)*PAGE_SIZE, filtered.length)} of {filtered.length}</span>
            <button style={S.btnSm} disabled={(page+1)*PAGE_SIZE>=filtered.length} onClick={()=>setPage(p=>p+1)}>Next →</button>
          </div>
        )}
      </>}

      {view === "suppliers" && (
        <div style={S.card}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--paper2)"}}>
                <th style={S.th()}>Supplier</th><th style={S.th()}>GSTIN</th><th style={S.th(true)}>Documents</th>
                <th style={S.th(true)}>Billed</th><th style={S.th(true)}>Paid</th><th style={S.th(true)}>Balance</th><th style={S.th()}></th>
              </tr></thead>
              <tbody>
                {supplierRows.length === 0 && <tr><td colSpan={7} style={{...S.td,textAlign:"center",color:"var(--ink3)",padding:30}}>No suppliers yet.</td></tr>}
                {supplierRows.map(s => (
                  <tr key={s.id} style={{borderTop:"1px solid var(--paper2)"}}>
                    <td style={S.td}>{s.name}</td>
                    <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{s.gstin||"—"}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{s.count}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(s.billed)}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(s.paid)}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{fmt(s.balance)}</td>
                    <td style={{...S.td,whiteSpace:"nowrap"}}><button style={S.btnSm} onClick={()=>{ setLedgerSupplier(s.id); setView("ledger"); }}>📒 Ledger</button>{" "}<button style={S.btnSm} onClick={()=>setPayModal({ supplierId:s.id })}>💳 Pay</button>{" "}<button style={S.btnSm} onClick={()=>{ setSearch(s.gstin||s.name); setView("invoices"); }}>Invoices</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "ledger" && (
        <div>
          <div style={{display:"flex",gap:12,alignItems:"flex-end",marginBottom:14,flexWrap:"wrap"}}>
            <div style={{width:340}}>
              <SupplierPicker key={ledgerSupplier || "none"} suppliers={suppliers} value={ledgerSupplier} onChange={setLedgerSupplier} label="Supplier"/>
            </div>
            {ledgerSupplier && <>
              <button style={S.btnPrimary} onClick={()=>setPayModal({ supplierId: ledgerSupplier })}>💳 Record payment</button>
              <button style={S.btnGhost} onClick={downloadLedgerCSV}>⬇ CSV</button>
              <div style={{marginLeft:"auto",fontFamily:"var(--mono)",fontSize:13}}>Balance payable <b>{fmt(ledger?.closing)}</b></div>
            </>}
          </div>
          {!ledgerSupplier && <div style={{...S.card,padding:30,textAlign:"center",color:"var(--ink3)",fontSize:13}}>Pick a supplier to see their running ledger of invoices and payments.</div>}
          {ledgerSupplier && ledger && (
            <div style={S.card}>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr style={{background:"var(--paper2)"}}>
                    <th style={S.th()}>Date</th><th style={S.th()}>Particulars</th><th style={S.th(true)}>Billed</th>
                    <th style={S.th(true)}>Paid</th><th style={S.th(true)}>Balance payable</th><th style={S.th()}></th>
                  </tr></thead>
                  <tbody>
                    {fromDate && <tr style={{background:"var(--paper2)"}}><td style={S.td}></td><td style={{...S.td,fontStyle:"italic"}}>Opening balance</td><td style={S.td}></td><td style={S.td}></td><td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(ledger.opening)}</td><td style={S.td}></td></tr>}
                    {ledger.entries.length === 0 && <tr><td colSpan={6} style={{...S.td,textAlign:"center",color:"var(--ink3)",padding:30}}>No entries in this period.</td></tr>}
                    {ledger.entries.map(e => (
                      <tr key={e.key} style={{borderTop:"1px solid var(--paper2)"}}>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{fmtDMY(e.date)}</td>
                        <td style={S.td}>{e.label}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{e.billed ? `${e.billed<0?"−":""}${fmt(Math.abs(e.billed))}` : ""}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",color:"var(--accent)"}}>{e.paid ? fmt(e.paid) : ""}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{fmt(e.balance)}</td>
                        <td style={{...S.td,whiteSpace:"nowrap"}}>
                          {e.pay && <><button style={S.btnSm} onClick={()=>setPayModal({ record:e.pay })}>✎</button>{" "}<button style={S.btnSm} onClick={()=>removePayment(e.pay)}>🗑</button></>}
                          {e.inv && <button style={S.btnSm} onClick={()=>setInvModal(e.inv)}>✎</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {view === "bank" && <BankTab suppliers={suppliers} rows={rows} payments={payments} fromDate={fromDate} toDate={toDate} onChanged={load} toast={toast}/>}

      {invModal && <InvoiceModal suppliers={suppliers} record={invModal === true ? null : invModal}
        onClose={()=>setInvModal(null)} onSaved={()=>{ setInvModal(null); toast("Saved."); load(); }}/>}
      {payModal && <PayModal suppliers={suppliers} rows={rows} supplierId={payModal.supplierId} invoice={payModal.invoice} record={payModal.record}
        onClose={()=>setPayModal(null)} onSaved={()=>{ setPayModal(null); toast("Payment saved."); load(); }}/>}
      {importOpen && <ImportModal existing={invoices} suppliers={suppliers} onClose={()=>setImportOpen(false)}
        onDone={(n, s) => { setImportOpen(false); toast(`Imported ${n} documents${s ? ` and ${s} new suppliers` : ""}.`); load(); }}/>}
    </div>
  );
}
